import { getFormErrorMessage } from "@core/utility/get-error-message";
import { HotelFormRule, HotelFormSchema } from "@factory/form/form.rule";
import { useFormDefaultHotel } from "@factory/form/form-default-hotel.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseHotelModel } from "@factory/model/query-model-hotel";
import {
  useQyDeleteHotel,
  useQyGetHotelById,
  useQyUpdateHotel,
} from "@factory/query/hotel.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditHotel() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultHotel();
  const navigate = useNavigate();
  const { mapUpdateHotel, mapDeleteHotel } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { hotelId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Hotel
  const handleGetApiSuccess = (data: GetResponseHotelModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];
    setValue("name", responseData?.name || "");
    setValue("withBreakfast", Boolean(responseData?.with_breakfast));
    setValue("location", responseData?.location || "");
    setValue("description", responseData?.description || "");
  };
  const {
    data: hotelResponse,
    isLoading,
    isError: hotelError,
  } = useQyGetHotelById(hotelId || "", handleGetApiSuccess);
  const hotelData = hotelResponse?.data?.[0];

  // API UPDATE Hotel
  const handleUpdateApiSuccess = () => {
    showSuccess("Hotel updated successfully");
  };
  const { mutate: updateHotel, isLoading: isEditLoading } = useQyUpdateHotel(
    handleUpdateApiSuccess
  );

  // API DELETE Hotel
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Hotel deleted successfully");
  };
  const { mutate: deleteHotel } = useQyDeleteHotel(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<HotelFormSchema>({
    defaultValues: getDefault(hotelData),
    resolver: zodResolver(HotelFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: HotelFormSchema) => {
    if (!hotelData || !hotelData.code) {
      throw new Error("No hotel data");
    }

    const formData = mapUpdateHotel(form, hotelData.code);
    updateHotel(formData);
  };
  const handleValidateError = (err: FieldErrors<HotelFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!hotelData || !hotelData.code) {
      throw new Error("No hotel data found");
    }

    const form = mapDeleteHotel(hotelData.code);
    deleteHotel(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    hotelId,
    hotelData,
    isLoading,
    hotelError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
